import React, { useState, useEffect } from 'react'
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  TrendingUp,
  Shield,
  Download,
  Copy,
  Check,
  Info,
  Zap
} from 'lucide-react'
import '../styles/enhanced-transaction-status.css'

export type TransactionStatus = 'pending' | 'processing' | 'confirmed' | 'failed' | 'expired' | 'cancelled'

export interface TransactionStatusData {
  id: string
  status: TransactionStatus
  amount: number
  stablecoinAmount: number
  stablecoinType: string
  currency: string
  provider: string
  settlementRail: string
  transactionHash?: string
  blockNumber?: number
  confirmations: number
  requiredConfirmations: number
  estimatedTime: string
  actualTime?: string
  createdAt: string
  updatedAt: string
  errorMessage?: string
  retryCount?: number
  maxRetries?: number
}

export interface PaymentReceipt {
  receiptId: string
  paymentId: string
  businessName: string
  customerName: string
  amount: number
  currency: string
  stablecoinAmount: number
  stablecoinType: string
  transactionHash: string
  settlementRail: string
  completedAt: string
  exchangeRate: number
  fees: {
    networkFee: number
    processingFee: number
    total: number
  }
}

interface ProfessionalTransactionStatusProps {
  transaction: TransactionStatusData
  onRetry?: () => void
  onCancel?: () => void
  onViewDetails?: () => void
  showActions?: boolean
  compact?: boolean
  className?: string
  customerView?: boolean
  businessName?: string
  customerName?: string
  onDownloadReceipt?: (receipt: PaymentReceipt) => void
}

export const ProfessionalTransactionStatus: React.FC<ProfessionalTransactionStatusProps> = ({
  transaction,
  onRetry,
  onCancel,
  onViewDetails,
  showActions = true,
  compact = false,
  className = '',
  customerView = false,
  businessName = 'StablePay Business',
  customerName = 'Customer',
  onDownloadReceipt
}) => {
  const [copiedHash, setCopiedHash] = useState(false)
  const [realTimeConfirmations, setRealTimeConfirmations] = useState(transaction.confirmations)

  // Real-time blockchain confirmation tracking (Requirement 4.1)
  useEffect(() => {
    if (transaction.status === 'pending' || transaction.status === 'processing') {
      const interval = setInterval(() => {
        // Simulate real-time confirmation updates
        setRealTimeConfirmations(prev => {
          const newConfirmations = Math.min(prev + 1, transaction.requiredConfirmations)
          return newConfirmations
        })
      }, 15000) // Update every 15 seconds for demo

      return () => clearInterval(interval)
    }
  }, [transaction.status, transaction.requiredConfirmations])

  // Copy transaction hash to clipboard
  const copyTransactionHash = async () => {
    if (transaction.transactionHash) {
      try {
        await navigator.clipboard.writeText(transaction.transactionHash)
        setCopiedHash(true)
        setTimeout(() => setCopiedHash(false), 2000)
      } catch (err) {
        console.error('Failed to copy transaction hash:', err)
      }
    }
  }

  // Generate payment receipt
  const generateReceipt = (): PaymentReceipt => {
    return {
      receiptId: `RCP-${Date.now()}`,
      paymentId: transaction.id,
      businessName,
      customerName,
      amount: transaction.amount,
      currency: transaction.currency,
      stablecoinAmount: transaction.stablecoinAmount,
      stablecoinType: transaction.stablecoinType,
      transactionHash: transaction.transactionHash || '',
      settlementRail: transaction.settlementRail,
      completedAt: transaction.updatedAt,
      exchangeRate: transaction.amount / transaction.stablecoinAmount,
      fees: {
        networkFee: 0.001,
        processingFee: transaction.amount * 0.005,
        total: 0.001 + (transaction.amount * 0.005)
      }
    }
  }
  const getStatusConfig = (status: TransactionStatus) => {
    const baseConfig = {
      pending: {
        icon: Clock,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        statusColor: 'professional-status--warning',
        title: customerView ? 'Payment Processing' : 'Payment Pending',
        description: customerView 
          ? 'Your payment is being confirmed on the blockchain. This usually takes a few minutes.'
          : 'Waiting for blockchain confirmation'
      },
      processing: {
        icon: RefreshCw,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        statusColor: 'professional-status--info',
        title: customerView ? 'Almost There!' : 'Processing Payment',
        description: customerView
          ? 'Your payment is being processed. We\'ll notify you once it\'s complete.'
          : 'Transaction is being processed'
      },
      confirmed: {
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        statusColor: 'professional-status--success',
        title: customerView ? 'Payment Successful!' : 'Payment Confirmed',
        description: customerView
          ? `Your payment has been successfully sent to ${businessName}. A receipt is available below.`
          : 'Transaction completed successfully'
      },
      failed: {
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        statusColor: 'professional-status--error',
        title: customerView ? 'Payment Failed' : 'Payment Failed',
        description: customerView
          ? 'We couldn\'t process your payment. Please try again or contact support if the problem persists.'
          : transaction.errorMessage || 'Transaction could not be completed'
      },
      expired: {
        icon: AlertTriangle,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        statusColor: 'professional-status--warning',
        title: customerView ? 'Payment Link Expired' : 'Payment Expired',
        description: customerView
          ? 'This payment link has expired. Please contact the business for a new payment link.'
          : 'Transaction window has expired'
      },
      cancelled: {
        icon: XCircle,
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        statusColor: 'professional-status--error',
        title: customerView ? 'Payment Cancelled' : 'Payment Cancelled',
        description: customerView
          ? 'Your payment was cancelled. No charges were made to your account.'
          : 'Transaction was cancelled'
      }
    }

    return baseConfig[status] || {
      icon: Clock,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      statusColor: 'professional-status--info',
      title: 'Unknown Status',
      description: 'Status information unavailable'
    }
  }

  const statusConfig = getStatusConfig(transaction.status)
  const StatusIcon = statusConfig.icon
  const isAnimated = transaction.status === 'pending' || transaction.status === 'processing'
  const currentConfirmations = customerView ? realTimeConfirmations : transaction.confirmations
  const progressPercentage = Math.min((currentConfirmations / transaction.requiredConfirmations) * 100, 100)

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-HK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getBlockchainExplorerUrl = (hash: string) => {
    // This would be configurable based on the stablecoin network
    return `https://etherscan.io/tx/${hash}`
  }

  if (compact) {
    return (
      <div className={`professional-transaction-status professional-transaction-status--compact ${className}`}>
        <div className="professional-transaction-status__compact-content">
          <div className="professional-transaction-status__compact-icon">
            <StatusIcon className={`w-4 h-4 ${statusConfig.color} ${isAnimated ? 'animate-spin' : ''}`} />
          </div>
          <div className="professional-transaction-status__compact-details">
            <div className="professional-transaction-status__compact-title">
              {statusConfig.title}
            </div>
            <div className="professional-transaction-status__compact-meta">
              {transaction.stablecoinAmount.toFixed(6)} {transaction.stablecoinType} • {transaction.settlementRail}
            </div>
          </div>
          <div className={`professional-status ${statusConfig.statusColor}`}>
            <span className="professional-status__indicator"></span>
            {transaction.status}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`professional-transaction-status ${className}`}>
      <div className={`professional-card ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
        {/* Header */}
        <div className="professional-transaction-status__header">
          <div className="professional-transaction-status__header-left">
            <div className="professional-transaction-status__icon">
              <StatusIcon className={`w-6 h-6 ${statusConfig.color} ${isAnimated ? 'animate-spin' : ''}`} />
            </div>
            <div className="professional-transaction-status__header-content">
              <h3 className="professional-transaction-status__title">
                {statusConfig.title}
              </h3>
              <p className="professional-transaction-status__description">
                {statusConfig.description}
              </p>
            </div>
          </div>
          <div className={`professional-status ${statusConfig.statusColor}`}>
            <span className="professional-status__indicator"></span>
            {transaction.status}
          </div>
        </div>

        {/* Transaction Details */}
        <div className="professional-transaction-status__details">
          <div className="professional-transaction-status__detail-grid">
            <div className="professional-transaction-status__detail-item">
              <span className="professional-transaction-status__detail-label">Amount:</span>
              <span className="professional-transaction-status__detail-value">
                {transaction.currency} {transaction.amount.toLocaleString()}
              </span>
            </div>
            <div className="professional-transaction-status__detail-item">
              <span className="professional-transaction-status__detail-label">Stablecoin:</span>
              <span className="professional-transaction-status__detail-value">
                {transaction.stablecoinAmount.toFixed(6)} {transaction.stablecoinType}
              </span>
            </div>
            <div className="professional-transaction-status__detail-item">
              <span className="professional-transaction-status__detail-label">Provider:</span>
              <span className="professional-transaction-status__detail-value">
                {transaction.provider}
              </span>
            </div>
            <div className="professional-transaction-status__detail-item">
              <span className="professional-transaction-status__detail-label">Settlement:</span>
              <span className="professional-transaction-status__detail-value">
                {transaction.settlementRail}
              </span>
            </div>
            <div className="professional-transaction-status__detail-item">
              <span className="professional-transaction-status__detail-label">Created:</span>
              <span className="professional-transaction-status__detail-value">
                {formatTime(transaction.createdAt)}
              </span>
            </div>
            <div className="professional-transaction-status__detail-item">
              <span className="professional-transaction-status__detail-label">Updated:</span>
              <span className="professional-transaction-status__detail-value">
                {formatTime(transaction.updatedAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Progress Indicator for Pending/Processing - Enhanced for Customer View (Requirement 4.2) */}
        {(transaction.status === 'pending' || transaction.status === 'processing') && (
          <div className="professional-transaction-status__progress">
            <div className="professional-transaction-status__progress-header">
              <span className="professional-transaction-status__progress-label">
                {customerView ? 'Blockchain Confirmation Progress' : 'Confirmation Progress'}
              </span>
              <span className="professional-transaction-status__progress-value">
                {currentConfirmations}/{transaction.requiredConfirmations} confirmations
              </span>
            </div>
            <div className="professional-transaction-status__progress-bar">
              <div
                className="professional-transaction-status__progress-fill"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <div className="professional-transaction-status__progress-time">
              <Clock className="w-4 h-4" />
              <span>Estimated time: {transaction.estimatedTime}</span>
            </div>
            
            {/* Customer-friendly explanation */}
            {customerView && (
              <div className="professional-transaction-status__progress-explanation">
                <Info className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600">
                  Each confirmation makes your payment more secure. Most payments complete within 15 minutes.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Blockchain Information - Enhanced with Customer-Friendly Explanations */}
        {transaction.transactionHash && (
          <div className="professional-transaction-status__blockchain">
            <div className="professional-transaction-status__blockchain-header">
              <Shield className="w-4 h-4 text-blue-600" />
              <span className="professional-transaction-status__blockchain-title">
                {customerView ? 'Transaction Details' : 'Blockchain Information'}
              </span>
            </div>
            <div className="professional-transaction-status__blockchain-details">
              <div className="professional-transaction-status__blockchain-item">
                <span className="professional-transaction-status__blockchain-label">
                  {customerView ? 'Transaction ID:' : 'Transaction Hash:'}
                </span>
                <div className="professional-transaction-status__blockchain-value">
                  <code className="professional-transaction-status__hash">
                    {transaction.transactionHash.slice(0, 10)}...{transaction.transactionHash.slice(-8)}
                  </code>
                  <button
                    onClick={copyTransactionHash}
                    className="professional-transaction-status__copy-btn"
                    title="Copy transaction hash"
                  >
                    {copiedHash ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <a
                    href={getBlockchainExplorerUrl(transaction.transactionHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="professional-transaction-status__explorer-link"
                    title={customerView ? 'View on blockchain explorer' : 'View on explorer'}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
              {transaction.blockNumber && (
                <div className="professional-transaction-status__blockchain-item">
                  <span className="professional-transaction-status__blockchain-label">
                    Block Number:
                  </span>
                  <span className="professional-transaction-status__blockchain-value">
                    {transaction.blockNumber.toLocaleString()}
                  </span>
                </div>
              )}
              
              {/* Customer explanation */}
              {customerView && (
                <div className="professional-transaction-status__blockchain-explanation">
                  <p className="text-sm text-gray-600">
                    This unique transaction ID proves your payment on the blockchain. 
                    You can use it to track your payment or as proof of payment.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settlement Tracking Section */}
        {(transaction.status === 'confirmed' || transaction.status === 'processing') && (
          <div className="professional-transaction-status__settlement">
            <div className="professional-transaction-status__settlement-header">
              <Shield className="w-4 h-4 text-blue-600" />
              <span className="professional-transaction-status__settlement-title">
                {customerView ? 'Settlement Status' : 'Business Settlement Tracking'}
              </span>
            </div>
            <div className="professional-transaction-status__settlement-content">
              <div className="professional-transaction-status__settlement-rail">
                <div className="professional-transaction-status__settlement-rail-info">
                  <span className="professional-transaction-status__settlement-label">Settlement Method:</span>
                  <span className="professional-transaction-status__settlement-value">
                    {transaction.settlementRail} - {
                      transaction.settlementRail === 'FPS' ? 'Faster Payment System' :
                      transaction.settlementRail === 'CIPS' ? 'Cross-border Interbank Payment System' :
                      'Digital Yuan (e-CNY)'
                    }
                  </span>
                </div>
                <div className="professional-transaction-status__settlement-timing">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span>
                    {customerView 
                      ? `Business will receive funds via ${transaction.settlementRail} within ${
                          transaction.settlementRail === 'FPS' ? '15 minutes' :
                          transaction.settlementRail === 'CIPS' ? 'same day' :
                          'instantly'
                        }`
                      : `Settlement to your ${transaction.settlementRail} account: ${
                          transaction.settlementRail === 'FPS' ? '15 minutes' :
                          transaction.settlementRail === 'CIPS' ? 'same day' :
                          'instant'
                        }`
                    }
                  </span>
                </div>
                
                {/* Settlement Progress for Business View */}
                {!customerView && (
                  <div className="professional-transaction-status__settlement-progress">
                    <div className="professional-transaction-status__settlement-steps">
                      <div className={`professional-transaction-status__settlement-step ${
                        transaction.status === 'confirmed' ? 'professional-transaction-status__settlement-step--completed' : 
                        'professional-transaction-status__settlement-step--pending'
                      }`}>
                        <CheckCircle className="w-4 h-4" />
                        <span>Payment Confirmed</span>
                      </div>
                      <div className={`professional-transaction-status__settlement-step ${
                        transaction.status === 'confirmed' ? 'professional-transaction-status__settlement-step--active' : 
                        'professional-transaction-status__settlement-step--pending'
                      }`}>
                        <RefreshCw className={`w-4 h-4 ${transaction.status === 'confirmed' ? 'animate-spin' : ''}`} />
                        <span>Processing Settlement</span>
                      </div>
                      <div className="professional-transaction-status__settlement-step professional-transaction-status__settlement-step--pending">
                        <Zap className="w-4 h-4" />
                        <span>Funds Received</span>
                      </div>
                    </div>
                    <div className="professional-transaction-status__settlement-account">
                      <span className="professional-transaction-status__settlement-label">Settlement Account:</span>
                      <span className="professional-transaction-status__settlement-value">
                        {transaction.settlementRail === 'FPS' ? 'HSBC-HK-***6789' :
                         transaction.settlementRail === 'CIPS' ? 'BOC-CN-***4321' :
                         'eCNY-WALLET-***6789'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Customer Settlement Notification */}
                {customerView && transaction.status === 'confirmed' && (
                  <div className="professional-transaction-status__customer-settlement">
                    <div className="professional-transaction-status__settlement-notification">
                      <Info className="w-4 h-4 text-blue-500" />
                      <div className="professional-transaction-status__notification-content">
                        <p className="professional-transaction-status__notification-title">
                          Settlement in Progress
                        </p>
                        <p className="professional-transaction-status__notification-text">
                          {businessName} will receive your payment in their {transaction.settlementRail} account within {
                            transaction.settlementRail === 'FPS' ? '15 minutes' :
                            transaction.settlementRail === 'CIPS' ? 'the same business day' :
                            'seconds'
                          }. They will be notified once the funds are available.
                        </p>
                      </div>
                    </div>
                    <div className="professional-transaction-status__settlement-benefits">
                      <div className="professional-transaction-status__benefit">
                        <Zap className="w-4 h-4 text-green-500" />
                        <span>
                          {transaction.settlementRail === 'FPS' ? '96% faster than SWIFT transfers' :
                           transaction.settlementRail === 'CIPS' ? '80% faster than correspondent banking' :
                           '99% faster than traditional transfers'}
                        </span>
                      </div>
                      <div className="professional-transaction-status__benefit">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span>
                          {((transaction.amount * 0.035) - (transaction.amount * 0.005)).toFixed(2)} {transaction.currency} saved vs traditional transfer
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Success Metrics and Receipt for Confirmed Transactions (Requirement 4.3) */}
        {transaction.status === 'confirmed' && (
          <div className="professional-transaction-status__success">
            <div className="professional-transaction-status__success-header">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="professional-transaction-status__success-title">
                {customerView ? 'Payment Complete!' : 'Transaction Completed'}
              </span>
            </div>
            
            {customerView ? (
              <div className="professional-transaction-status__receipt">
                <div className="professional-transaction-status__receipt-header">
                  <h4 className="professional-transaction-status__receipt-title">Payment Receipt</h4>
                  <div className="professional-transaction-status__receipt-actions">
                    {onDownloadReceipt && (
                      <button
                        onClick={() => onDownloadReceipt(generateReceipt())}
                        className="professional-btn professional-btn--secondary professional-btn--sm"
                      >
                        <Download className="w-4 h-4" />
                        Download Receipt
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="professional-transaction-status__receipt-details">
                  <div className="professional-transaction-status__receipt-section">
                    <h5 className="professional-transaction-status__receipt-section-title">Payment Summary</h5>
                    <div className="professional-transaction-status__receipt-items">
                      <div className="professional-transaction-status__receipt-item">
                        <span>Paid to:</span>
                        <span className="font-semibold">{businessName}</span>
                      </div>
                      <div className="professional-transaction-status__receipt-item">
                        <span>Amount:</span>
                        <span className="font-semibold">{transaction.currency} {transaction.amount.toLocaleString()}</span>
                      </div>
                      <div className="professional-transaction-status__receipt-item">
                        <span>Paid with:</span>
                        <span className="font-semibold">{transaction.stablecoinAmount.toFixed(6)} {transaction.stablecoinType}</span>
                      </div>
                      <div className="professional-transaction-status__receipt-item">
                        <span>Settlement method:</span>
                        <span className="font-semibold">{transaction.settlementRail}</span>
                      </div>
                      <div className="professional-transaction-status__receipt-item">
                        <span>Completed at:</span>
                        <span className="font-semibold">{formatTime(transaction.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="professional-transaction-status__receipt-section">
                    <h5 className="professional-transaction-status__receipt-section-title">Speed & Savings</h5>
                    <div className="professional-transaction-status__receipt-benefits">
                      <div className="professional-transaction-status__benefit-item">
                        <Zap className="w-4 h-4 text-green-500" />
                        <span>Completed in {transaction.actualTime || '15 minutes'} vs 3-5 days traditional transfer</span>
                      </div>
                      <div className="professional-transaction-status__benefit-item">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span>Saved ~{((transaction.amount * 0.04) - (transaction.amount * 0.005)).toFixed(2)} {transaction.currency} in fees</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="professional-transaction-status__success-details">
                <div className="professional-transaction-status__success-item">
                  <span>Actual settlement time:</span>
                  <span className="professional-transaction-status__success-value">
                    {transaction.actualTime || '15 minutes'}
                  </span>
                </div>
                <div className="professional-transaction-status__success-item">
                  <span>Final confirmations:</span>
                  <span className="professional-transaction-status__success-value">
                    {currentConfirmations}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions - Enhanced for Customer View (Requirement 4.4) */}
        {showActions && (
          <div className="professional-transaction-status__actions">
            {transaction.status === 'failed' && onRetry && (
              <div className="professional-transaction-status__retry-section">
                {customerView && (
                  <div className="professional-transaction-status__error-help">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    <div className="professional-transaction-status__error-content">
                      <p className="font-medium">What went wrong?</p>
                      <p className="text-sm text-gray-600">
                        {transaction.errorMessage || 'The payment could not be processed. This might be due to insufficient balance, network issues, or temporary provider problems.'}
                      </p>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={onRetry}
                  disabled={transaction.retryCount && transaction.maxRetries && transaction.retryCount >= transaction.maxRetries}
                  className="professional-btn professional-btn--primary"
                >
                  <RefreshCw className="w-4 h-4" />
                  {customerView ? 'Try Payment Again' : 'Retry Payment'}
                  {transaction.retryCount && transaction.maxRetries && (
                    <span className="professional-transaction-status__retry-count">
                      ({transaction.maxRetries - transaction.retryCount} attempts left)
                    </span>
                  )}
                </button>
                
                {customerView && (
                  <p className="text-xs text-gray-500 mt-2">
                    Need help? Contact support with transaction ID: {transaction.id}
                  </p>
                )}
              </div>
            )}

            {(transaction.status === 'pending' || transaction.status === 'processing') && onCancel && (
              <button
                onClick={onCancel}
                className="professional-btn professional-btn--secondary"
              >
                {customerView ? 'Cancel Payment' : 'Cancel Payment'}
              </button>
            )}

            {onViewDetails && !customerView && (
              <button
                onClick={onViewDetails}
                className="professional-btn professional-btn--secondary"
              >
                View Details
              </button>
            )}
            
            {/* Customer-specific success actions */}
            {customerView && transaction.status === 'confirmed' && (
              <div className="professional-transaction-status__success-actions">
                <p className="text-sm text-gray-600 text-center">
                  Payment successfully sent to {businessName}. 
                  Keep this receipt for your records.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}